import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material'
import debounce from 'lodash/debounce'
import { forwardRef, MouseEvent, useRef, useState } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import {
  Icons,
  ScrollTag,
  SearchInputField,
  SearchInputFieldProps,
} from 'utoniq-ui'

import { PostType, StoryFilter } from '@/clients/utoniq-core/schema'
import { Form, useForm, UseFormProps, useTranslation } from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { individualShowStory } from '@/core/individual'

export type SearchFormProps = {
  onSubmit: (values: SearchFormValues) => Promise<void>
  hiddenFilter: boolean
}

export type SearchFormValues = {
  keyword: string
  /**
   * @description
   * 'story' の値が選択された場合
   * type は検索条件に使用されない
   * 代わりに types と storyFilter が検索条件として利用される
   */
  type?: PostType | 'story'
  types?: PostType[]
  storyFilter?: StoryFilter
  filter: string
}

const schema: UseFormProps['schema'] = o => ({
  keyword: o.string().allow('').optional(),
  type: o.string().allow('').optional(),
  types: o.array().items(o.string()).optional(),
  storyFilter: o.string().allow('').optional(),
  filter: o.string().allow('').optional(),
})

const defaultValues: SearchFormValues = {
  keyword: '',
  type: undefined,
  types: undefined,
  storyFilter: undefined,
  filter: 'newestOrder',
}

export const SearchForm: Organism<SearchFormProps> = ({
  onSubmit,
  hiddenFilter,
}) => {
  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    formState: { isSubmitted },
  } = useForm<SearchFormValues>({
    schema,
    defaultValues,
  })
  const handleChangeKeywordDebounce = useRef(
    debounce(
      handleSubmit(({ keyword }) => onSubmit({ ...defaultValues, keyword })),
      500
    )
  )

  return (
    <OrganismFrame sx={{ px: 5, pt: 4 }}>
      <Form onSubmit={handleSubmit(onSubmit)} sx={{ bgcolor: 'transparent' }}>
        {/**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-5197
         */}
        {/*<SearchInputCell*/}
        {/*  {...register('keyword')}*/}
        {/*  onChange={async event => {*/}
        {/*    setValue('keyword', event.target.value)*/}
        {/*    await handleChangeKeywordDebounce.current()*/}
        {/*  }}*/}
        {/*/>*/}
        {!(getValues().keyword.length && isSubmitted) && (
          <>
            <ScrollTagCell
              control={control}
              setValue={setValue}
              onChange={handleSubmit(({ type, ...rest }) => {
                const buildSearchParams = () => {
                  // 全選択の場合: 全てのポストを取得（ストーリーも含む）
                  if (type === undefined) {
                    return {
                      ...rest,
                      storyFilter: StoryFilter.Include,
                    }
                  }

                  // ストーリー選択の場合: ストーリーのみを取得
                  if (type === 'story') {
                    return {
                      ...rest,
                      types: [PostType.Video, PostType.Image],
                      storyFilter: StoryFilter.Only,
                    }
                  }

                  // 特定のタイプ選択の場合: 通常のポストのみを取得
                  return { ...rest, type }
                }

                onSubmit(buildSearchParams())
              })}
            />
            {/**
             * @description
             * Open when function is implemented in the future
             * https://app.clickup.com/t/908127/UT-5197
             */}
            {/*{!hiddenFilter && (*/}
            {/*  <FilterCell*/}
            {/*    setValue={setValue}*/}
            {/*    onChange={handleSubmit(onSubmit)}*/}
            {/*  />*/}
            {/*)}*/}
          </>
        )}
      </Form>
    </OrganismFrame>
  )
}

const SearchInputCell = forwardRef(
  (props: Omit<SearchInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consolePosts')
    return (
      <CellFrame sx={{ width: '100%' }}>
        <SearchInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type ScrollTagCellProps = {
  control: Control<SearchFormValues>
  setValue: UseFormSetValue<SearchFormValues>
  onChange: () => void
}
const ScrollTagCell: Cell<ScrollTagCellProps> = ({
  control,
  setValue,
  onChange,
}) => {
  const { t } = useTranslation('consolePosts')
  const type = useWatch({ control, name: 'type' })

  const handleTagClick = (
    clickedValue: PostType | 'story' | undefined,
    currentType: PostType | 'story' | undefined,
    setValue: UseFormSetValue<SearchFormValues>,
    onChange: () => void
  ) => {
    // 同じタグをクリックした場合はリセット（undefined）、違うタグをクリックした場合は新しい値を設定
    const newType = currentType === clickedValue ? undefined : clickedValue

    // フォームのフィールドを更新
    setValue('type', newType)

    // フォームの値を送信
    onChange()
  }

  const typeOptions: {
    value: PostType | 'story' | undefined
    label: string
  }[] = [
    { value: undefined, label: t('all') },
    { value: PostType.Video, label: t('video') },
    { value: PostType.Sound, label: t('sound') },
    { value: PostType.Image, label: t('image') },
    { value: PostType.Text, label: t('text') },
    ...(individualShowStory()
      ? [{ value: 'story', label: t('story') } as const]
      : []),
  ]

  return (
    <CellFrame className="いむ">
      <ScrollTag
        value={type}
        tags={typeOptions}
        onClick={value => handleTagClick(value, type, setValue, onChange)}
      />
    </CellFrame>
  )
}

type FilterOptions = Record<'value' | 'label', string>[]

type FilterCellProps = {
  setValue: UseFormSetValue<SearchFormValues>
  onChange: () => void
}
const FilterCell: Cell<FilterCellProps> = ({ setValue, onChange }) => {
  const { t } = useTranslation('consolePosts')
  const filterOptions: FilterOptions = [
    { value: 'newestOrder', label: t('newestOrder') },
    { value: 'oldestOrder', label: t('oldestOrder') },
  ]
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedOption, setSelectedOption] = useState<FilterOptions[number]>(
    filterOptions[0]
  )
  const open = Boolean(anchorEl)

  const handleClickListItem = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuItemClick = (
    event: MouseEvent<HTMLElement>,
    option: FilterOptions[number]
  ) => {
    setSelectedOption(option)
    setValue('filter', option.value)
    onChange()
    setAnchorEl(null)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <CellFrame sx={{ py: 1 }}>
      <Stack direction="row-reverse" spacing={2}>
        <Button variant="text" sx={{ mr: -2 }} onClick={handleClickListItem}>
          <Typography variant="caption" sx={{ mr: 2 }}>
            {selectedOption.label}
          </Typography>
          <Icons.DownArrow fontSize="xSmall" />
        </Button>
      </Stack>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {filterOptions.map((option, index) => (
          <MenuItem
            key={option.value}
            selected={option.value === selectedOption.value}
            onClick={event => handleMenuItemClick(event, option)}
          >
            <Typography variant="body2">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </CellFrame>
  )
}
